import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { db, auth } from "../firebase";
import {
  doc,
  getDoc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import {
  Calendar,
  Clock,
  Mail,
  Phone,
  User,
  ArrowLeft,
  CheckCircle2,
  XCircle,
  Stethoscope,
  Info,
} from "lucide-react";

function getStatusStyle(status) {
  switch (status) {
    case "confirmed":
      return { bg: "#dcfce7", fg: "#166534" };
    case "pending":
      return { bg: "#fef3c7", fg: "#92400e" };
    case "completed":
      return { bg: "#dbeafe", fg: "#1e40af" };
    case "cancelled":
      return { bg: "#fee2e2", fg: "#991b1b" };
    default:
      return { bg: "#f3f4f6", fg: "#374151" };
  }
}

function formatTimeMaybe(timeString) {
  if (!timeString) return "Time TBD";
  try {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString([], {
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return timeString;
  }
}

/**
 * AppointmentView
 *
 * Can be used in 2 ways:
 * 1) As a ROUTE component:
 *    <Route path="/doctor/appointments/:appointmentId" element={<AppointmentView mode="doctor" />} />
 *    <Route path="/appointments/:appointmentId" element={<AppointmentView mode="patient" />} />
 *
 * 2) As an INLINE component:
 *    <AppointmentView appointmentId={id} mode="patient" onClose={...} />
 */
export default function AppointmentView({
  appointmentId: propAppointmentId,
  mode = "doctor", // "doctor" or "patient"
  onClose,         // optional callback for Back button when used inline
}) {
  const params = useParams();
  const navigate = useNavigate();
  const [user, authLoading] = useAuthState(auth);

  // prefer prop, fallback to :appointmentId route param
  const appointmentId = propAppointmentId || params.appointmentId;

  const [apt, setApt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [updating, setUpdating] = useState(false);

  const statusStyle = useMemo(
    () => getStatusStyle(apt?.status),
    [apt?.status]
  );

  const load = useCallback(async () => {
    if (!appointmentId) return;
    setLoading(true);
    setErr(null);
    try {
      const snap = await getDoc(doc(db, "appointments", appointmentId));
      if (!snap.exists()) {
        setErr("Appointment not found.");
        setApt(null);
      } else {
        setApt({ id: snap.id, ...snap.data() });
      }
    } catch (e) {
      setErr(e.message || "Failed to load appointment.");
    } finally {
      setLoading(false);
    }
  }, [appointmentId]);

  useEffect(() => {
    load();
  }, [load]);

  // --- Ownership / access control -----------------------------

  const { isDoctorOfApt, isPatientOfApt, isAllowed } = useMemo(() => {
    if (!user || !apt) {
      return { isDoctorOfApt: false, isPatientOfApt: false, isAllowed: false };
    }

    const uid = user.uid;

    // Try to resolve patient + doctor ids from different possible fields
    const patientId =
      apt.patientId ||
      apt.patientUid ||
      apt.patientUID ||
      apt.patient?.id ||
      apt.patientInfo?.id ||
      apt.patientInfo?.uid;

    const doctorId =
      apt.doctorId ||
      apt.doctorUid ||
      apt.doctorUID ||
      apt.doctor?.id ||
      apt.doctorInfo?.id ||
      apt.doctorInfo?.uid;

    const isPatientOfApt = !!patientId && patientId === uid;
    const isDoctorOfApt = !!doctorId && doctorId === uid;

    const isAllowed =
      (mode === "doctor" && isDoctorOfApt) ||
      (mode === "patient" && isPatientOfApt);

    return { isDoctorOfApt, isPatientOfApt, isAllowed };
  }, [user, apt, mode]);

  async function setStatus(newStatus) {
    // Only the doctor of this appointment can change status
    if (!apt?.id || mode !== "doctor" || !isDoctorOfApt) return;

    setUpdating(true);
    try {
      await updateDoc(doc(db, "appointments", apt.id), {
        status: newStatus,
        updatedAt: serverTimestamp(),
        ...(newStatus === "confirmed" && { confirmedAt: serverTimestamp() }),
        ...(newStatus === "cancelled" && { cancelledAt: serverTimestamp() }),
        ...(newStatus === "completed" && { completedAt: serverTimestamp() }),
      });
      await load();
    } catch (e) {
      alert("Failed to update status. Check rules/connection and try again.");
    } finally {
      setUpdating(false);
    }
  }

  const handleBack = () => {
    if (onClose) onClose();
    else navigate(-1);
  };

  // --- Top-level loading / auth gate --------------------------

  if (authLoading || (loading && !apt && !err)) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily:
            "'Inter', system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
          background:
            "linear-gradient(135deg,#f0f9ff 0%,#ffffff 30%,#f0fdf4 100%)",
        }}
      >
        <div style={{ color: "#6b7280" }}>Loading appointment…</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontFamily:
            "'Inter', system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
          background:
            "linear-gradient(135deg,#f0f9ff 0%,#ffffff 30%,#f0fdf4 100%)",
        }}
      >
        <div
          style={{
            padding: 16,
            borderRadius: 12,
            background: "#fef2f2",
            border: "1px solid #fee2e2",
            color: "#991b1b",
          }}
        >
          You must be logged in to view this appointment.
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg,#f0f9ff 0%,#ffffff 30%,#f0fdf4 100%)",
        fontFamily:
          "'Inter', system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
        padding: "16px",
      }}
    >
      <div style={{ maxWidth: 880, margin: "0 auto" }}>
        {/* Top bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 16,
          }}
        >
          <button
            onClick={handleBack}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 12px",
              borderRadius: 10,
              border: "1px solid #e5e7eb",
              background: "white",
              cursor: "pointer",
            }}
          >
            <ArrowLeft size={18} />
            <span>Back</span>
          </button>

          {mode === "doctor" && (
            <Link
              to="/dashboard-doctor"
              style={{
                textDecoration: "none",
                padding: "10px 12px",
                borderRadius: 10,
                border: "1px solid #d1d5db",
                background: "white",
                color: "#111827",
                fontWeight: 600,
              }}
            >
              Open Dashboard
            </Link>
          )}
        </div>

        {/* Card */}
        <div
          style={{
            background: "rgba(255,255,255,.9)",
            borderRadius: 20,
            border: "1px solid rgba(229,231,235,.8)",
            boxShadow: "0 10px 25px -5px rgba(0,0,0,.1)",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "18px 20px",
              borderBottom: "1px solid #eef2f7",
              background: "linear-gradient(135deg,#f8fafc,#f1f5f9)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg,#3b82f6,#6366f1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                }}
              >
                <Stethoscope size={22} />
              </div>
              <div>
                <div
                  style={{
                    fontSize: 18,
                    fontWeight: 700,
                    color: "#111827",
                  }}
                >
                  Appointment
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  ID: {appointmentId || "—"}
                </div>
              </div>
            </div>

            {apt?.status && (
              <span
                style={{
                  padding: "6px 12px",
                  borderRadius: 999,
                  background: statusStyle.bg,
                  color: statusStyle.fg,
                  fontWeight: 600,
                  fontSize: 12,
                }}
              >
                {apt.status}
              </span>
            )}
          </div>

          {/* Body */}
          <div style={{ display: "grid", gap: 16, padding: 20 }}>
            {/* Firestore / load error */}
            {err && (
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  padding: 16,
                  border: "1px solid #fee2e2",
                  background: "#fef2f2",
                  borderRadius: 12,
                  color: "#991b1b",
                }}
              >
                <Info size={18} />
                <span>{err}</span>
              </div>
            )}

            {/* Access denied */}
            {!err && apt && !isAllowed && (
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  padding: 16,
                  border: "1px solid #fee2e2",
                  background: "#fef2f2",
                  borderRadius: 12,
                  color: "#991b1b",
                }}
              >
                <Info size={18} />
                <span>
                  You don’t have permission to view this appointment.
                </span>
              </div>
            )}

            {/* Appointment data (only if allowed) */}
            {!err && apt && isAllowed && (
              <>
                {/* Patient */}
                <Section title="Patient">
                  <Row
                    icon={<User size={16} />}
                    label="Name"
                    value={apt.patientInfo?.name || "Unknown"}
                  />
                  <Row
                    icon={<Mail size={16} />}
                    label="Email"
                    value={apt.patientInfo?.email || "Unknown"}
                  />
                  {apt.patientInfo?.phone && (
                    <Row
                      icon={<Phone size={16} />}
                      label="Phone"
                      value={apt.patientInfo.phone}
                    />
                  )}
                </Section>

                {/* Appointment */}
                <Section title="Appointment">
                  <Row
                    icon={<Calendar size={16} />}
                    label="Date"
                    value={
                      apt.scheduledDate
                        ? new Date(apt.scheduledDate).toLocaleDateString(
                            [],
                            {
                              weekday: "short",
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            }
                          )
                        : "Date TBD"
                    }
                  />
                  <Row
                    icon={<Clock size={16} />}
                    label="Time"
                    value={formatTimeMaybe(apt.scheduledTime)}
                  />
                  <Row
                    label="Type"
                    value={apt.appointmentType || "in-person"}
                  />
                  <Row
                    label="Service"
                    value={apt.serviceName || apt.service || "—"}
                  />
                  {apt.servicePrice && (
                    <Row label="Price" value={apt.servicePrice} />
                  )}
                </Section>

                {/* Reason & notes */}
                {(apt.reason || apt.notes) && (
                  <Section title="Details">
                    {apt.reason && (
                      <BoxLabel label="Reason">{apt.reason}</BoxLabel>
                    )}
                    {apt.notes && (
                      <BoxLabel label="Patient Notes">{apt.notes}</BoxLabel>
                    )}
                  </Section>
                )}

                {/* Actions (only doctor owning this appointment) */}
                {mode === "doctor" && isDoctorOfApt && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 10,
                      paddingTop: 8,
                      borderTop: "1px solid #f3f4f6",
                    }}
                  >
                    {apt.status === "pending" && (
                      <>
                        <ActionBtn
                          onClick={() => setStatus("confirmed")}
                          disabled={updating}
                          kind="primary"
                          icon={<CheckCircle2 size={16} />}
                          label="Confirm"
                        />
                        <ActionBtn
                          onClick={() => setStatus("cancelled")}
                          disabled={updating}
                          kind="danger"
                          icon={<XCircle size={16} />}
                          label="Decline"
                        />
                      </>
                    )}

                    {apt.status === "confirmed" && (
                      <>
                        <ActionBtn
                          onClick={() => setStatus("completed")}
                          disabled={updating}
                          kind="primary"
                          icon={<CheckCircle2 size={16} />}
                          label="Mark Completed"
                        />
                      </>
                    )}

                    <Link
                      to="/dashboard-doctor"
                      style={{
                        textDecoration: "none",
                        padding: "10px 14px",
                        borderRadius: 10,
                        border: "1px solid #d1d5db",
                        background: "#ffffff",
                        color: "#1f2937",
                        fontWeight: 600,
                      }}
                    >
                      Open in Dashboard
                    </Link>
                  </div>
                )}

                {/* Permalink (only useful for doctor mode) */}
                {mode === "doctor" && isDoctorOfApt && (
                  <div
                    style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}
                  >
                    Link: {window.location.origin}/doctor/appointments/{apt.id}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Mobile spacing */}
        <div style={{ height: 24 }} />
      </div>
    </div>
  );
}

/* ---------- Small presentational helpers ---------- */

function Section({ title, children }) {
  return (
    <div
      style={{
        background: "#ffffff",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        padding: 14,
      }}
    >
      <div
        style={{
          fontWeight: 700,
          color: "#111827",
          marginBottom: 10,
        }}
      >
        {title}
      </div>
      <div style={{ display: "grid", gap: 10 }}>{children}</div>
    </div>
  );
}

function Row({ icon, label, value }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        alignItems: "center",
        color: "#374151",
      }}
    >
      {icon && (
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#f3f4f6",
            color: "#6b7280",
          }}
        >
          {icon}
        </div>
      )}
      <div style={{ fontSize: 14 }}>
        <div style={{ fontSize: 12, color: "#6b7280" }}>{label}</div>
        <div style={{ fontWeight: 600 }}>{value}</div>
      </div>
    </div>
  );
}

function BoxLabel({ label, children }) {
  return (
    <div>
      <div
        style={{
          fontSize: 12,
          color: "#6b7280",
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div
        style={{
          border: "1px solid #e5e7eb",
          background: "#f9fafb",
          borderRadius: 10,
          padding: 12,
          color: "#374151",
          fontSize: 14,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function ActionBtn({ onClick, label, icon, kind = "primary", disabled }) {
  const styles =
    {
      primary: {
        bg: "linear-gradient(135deg,#10b981,#059669)",
        border: "none",
        color: "#fff",
      },
      danger: {
        bg: "linear-gradient(135deg,#ef4444,#dc2626)",
        border: "none",
        color: "#fff",
      },
    }[kind] || {};

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 8,
        padding: "10px 14px",
        borderRadius: 10,
        background: styles.bg,
        color: styles.color,
        border: styles.border,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.7 : 1,
        fontWeight: 700,
      }}
    >
      {icon}
      {label}
    </button>
  );
}
